import { pickT } from "@repo/shared";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { VegaEmbed } from "react-vega";
import { useContainerWidth } from "@/lib/hooks/use-container-width";
import { useUserLanguage } from "@/lib/i18n/context";
import type { ForecastChartData } from "@/lib/utils/vega-chart-shared";
import { T } from "./forecast-chart.i18n";
import { createForecastChartSpec } from "./vega-specs";

type ForecastChartProps = {
  data: ForecastChartData[];
  width?: number | "container";
  height?: number;
  title?: string;
  yAxisLabel?: string;
  primaryColor?: string;
  lang?: string;
  /** Rate behind the bank comparison series — shown in the legend label. */
  bankReturnPct?: number;
};

/**
 * Client-side forecast chart component using Vega-Lite
 *
 * Displays a line chart with confidence bounds (shaded area).
 * Used for portfolio forecast projections; the band runs from the 5th to the 95th percentile.
 */
export function ForecastChart({
  data,
  width = "container",
  height = 300,
  title,
  yAxisLabel,
  primaryColor,
  lang,
  bankReturnPct,
}: ForecastChartProps) {
  const { userLanguage } = useUserLanguage();
  // Use provided lang for chart content, fallback to user language
  const chartLang = lang || userLanguage;
  const t = pickT(T, chartLang);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  // When width is "container", measure the wrapper and render at that pixel
  // width so the chart fills its column / full-width slot (see useContainerWidth).
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();
  const effectiveWidth = typeof width === "number" ? width : containerWidth;

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === "dark";

  // Generate spec on client side and ensure it's serializable
  const spec = useMemo(() => {
    if (!data || data.length === 0 || effectiveWidth === null) {
      return null;
    }

    const chartSpec = createForecastChartSpec(data, {
      width: effectiveWidth,
      height,
      title,
      yAxisLabel,
      isDark,
      primaryColor,
      lang: chartLang,
      bankReturnPct,
    });

    // Ensure the spec is JSON-serializable by parsing/stringifying
    return JSON.parse(JSON.stringify(chartSpec));
  }, [
    data,
    effectiveWidth,
    height,
    title,
    yAxisLabel,
    isDark,
    primaryColor,
    chartLang,
    bankReturnPct,
  ]);

  if (!data || data.length === 0) {
    return (
      <div className="w-full p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noForecastData}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full [&_svg]:max-w-full [&_svg]:h-auto"
      style={{ minHeight: height }}
    >
      {spec && (
        <VegaEmbed
          spec={spec}
          options={{
            actions: false,
            renderer: "svg",
          }}
        />
      )}
    </div>
  );
}
